import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AuditLogEntry, listAuditLog, Organization, listOrganizations } from '../api/client'

const cardStyle: React.CSSProperties = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse' }
const thStyle: React.CSSProperties = {
  padding: '8px 12px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6b7280',
  textTransform: 'uppercase', borderBottom: '1px solid #e5e7eb', background: '#f9fafb',
}
const tdStyle: React.CSSProperties = { padding: '9px 12px', borderBottom: '1px solid #f3f4f6', fontSize: 13 }
const btnStyle: React.CSSProperties = { padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500 }

const PAGE_SIZE = 200
const MAX_LIMIT = 1000 // matches the server's cap in handleAuditLog

export default function AuditLog() {
  const [limit, setLimit] = useState(PAGE_SIZE)
  const { data: entries = [], isLoading, error, isFetching } = useQuery<AuditLogEntry[]>({
    queryKey: ['audit-log', limit],
    queryFn: () => listAuditLog(limit),
    refetchInterval: 30_000,
  })
  const { data: orgs = [] } = useQuery<Organization[]>({ queryKey: ['organizations'], queryFn: listOrganizations })
  const orgName = (id: number | null) => (id == null ? '—' : orgs.find(o => o.ID === id)?.Name ?? `#${id}`)

  const [search, setSearch] = useState('')
  const [resultFilter, setResultFilter] = useState<'' | 'success' | 'failure'>('')
  const searchLower = search.trim().toLowerCase()
  const filtered = entries.filter(e => {
    if (resultFilter === 'success' && !e.Success) return false
    if (resultFilter === 'failure' && e.Success) return false
    if (!searchLower) return true
    return (
      e.ActorEmail.toLowerCase().includes(searchLower) ||
      e.Action.toLowerCase().includes(searchLower) ||
      e.TargetType.toLowerCase().includes(searchLower) ||
      e.TargetID.toLowerCase().includes(searchLower) ||
      e.Detail.toLowerCase().includes(searchLower)
    )
  })

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>Audit Log</h3>
        <span style={{ fontSize: 13, color: '#6b7280' }}>
          {filtered.length === entries.length ? `${entries.length} entries` : `${filtered.length} of ${entries.length}`}
          {' '}· auto-refreshes every 30s
        </span>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <input
          style={{ flex: 1, maxWidth: 360, padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
          placeholder="Search by actor, action, target, or detail…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          style={{ padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
          value={resultFilter}
          onChange={(e) => setResultFilter(e.target.value as '' | 'success' | 'failure')}
        >
          <option value="">All results</option>
          <option value="success">Success only</option>
          <option value="failure">Failure only</option>
        </select>
        {(search || resultFilter) && (
          <button style={{ ...btnStyle, background: 'none', color: '#6b7280' }} onClick={() => { setSearch(''); setResultFilter('') }}>
            Clear filters
          </button>
        )}
      </div>

      {isLoading && <p style={{ color: '#6b7280' }}>Loading…</p>}
      {error && <p style={{ color: '#dc2626' }}>Error loading audit log: {String(error)}</p>}

      {!isLoading && !error && (
        <div style={cardStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Time</th>
                <th style={thStyle}>Actor</th>
                <th style={thStyle}>Organization</th>
                <th style={thStyle}>Action</th>
                <th style={thStyle}>Target</th>
                <th style={thStyle}>Detail</th>
                <th style={thStyle}>Result</th>
                <th style={thStyle}>IP</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af', padding: 24 }}>
                  {entries.length === 0 ? 'No audit entries yet.' : 'No entries match your filters.'}
                </td></tr>
              )}
              {filtered.map(e => (
                <tr key={e.ID}>
                  <td style={{ ...tdStyle, color: '#6b7280', whiteSpace: 'nowrap' }}>{new Date(e.CreatedAt).toLocaleString()}</td>
                  <td style={tdStyle}>{e.ActorEmail || '—'}</td>
                  <td style={tdStyle}>{orgName(e.OrgID)}</td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{e.Action}</td>
                  <td style={tdStyle}>
                    {e.TargetType ? `${e.TargetType}${e.TargetID ? ` #${e.TargetID}` : ''}` : '—'}
                  </td>
                  <td style={{ ...tdStyle, color: '#6b7280' }}>{e.Detail || '—'}</td>
                  <td style={tdStyle}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 9999, fontSize: 12, fontWeight: 600,
                      background: e.Success ? '#d1fae5' : '#fee2e2',
                      color: e.Success ? '#065f46' : '#991b1b',
                    }}>
                      {e.Success ? 'Success' : 'Failure'}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12, color: '#6b7280' }}>{e.IPAddress || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {entries.length === limit && limit < MAX_LIMIT && (
            <div style={{ padding: 12, textAlign: 'center', borderTop: '1px solid #f3f4f6' }}>
              <button
                style={{ ...btnStyle, background: '#f3f4f6', color: '#374151' }}
                onClick={() => setLimit(l => Math.min(l + PAGE_SIZE, MAX_LIMIT))}
                disabled={isFetching}
              >
                {isFetching ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
